import { Icon } from "@iconify/react";
import { CircularProgress } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import AlertDialog from "../../components/shared/AlertDialog";
import { CollapsibleInfoCard } from "../../components/shared/CollapsibleInfoCard";
import { ExpandButton } from "../../components/shared/ExpandButton";
import InfoBox from "../../components/shared/InfoBox";
import { Input, Label, TextArea } from "../../components/shared/InputField";
import { SwitchButton } from "../../components/shared/SwitchButton";
import {
  Box,
  ButtonPrimary,
  ButtonPrimary1,
  ButtonSecondary,
  decodeAuthToken,
  Error,
  HStack,
  isAdmin,
  VStack,
} from "../../components/utils";
import {
  useChangeOptionStates,
  useCreateESignRequest,
  useEsopPlans,
  useGrant,
  useSignAndApproveGrant,
} from "../../queries";
import { useGetGrantApprovalStatus } from "../../queries/grantApproval";
import {
  useEvaluateMatrixQuery,
  useGetAllMatrices,
} from "../../queries/allocationMatrix";
import { useGetEmailSettings } from "../../queries/siteSettings";
import { useGetEsopPlanDetails } from "../../queries/esopPlan";
import { useAuthStore, useCompanyStore } from "../../store";
import { useError } from "../../store/errorStore";
import {
  AllocationMode,
  CostOrGain,
  QuantumMode,
  RuleScope,
} from "../../store/useAllocationMatrixStore";
import {
  useGrantCreatedDialog,
  useSignaturePadDialog,
} from "../../store/useDialogStore";
import { PlanState } from "../../types/EsopPlan";
import { Grant, SignatureAttachment } from "../../types/Grant";
import { ErrorInterface } from "../../types/OptionBuyback";
import { formatCurrency, getCurrencyType } from "../../utils/currencyFormatter";
import { formatDisplayDate } from "../../utils/date";
import {
  capitalizeEachWord,
  getFormattedValue,
  stringFormat,
} from "../../utils/string";
import StatusLabel from "./StatusLabel";
import GrantOfferAcceptanceBadge from "./GrantOfferAcceptanceBadge";
import { GrantApprovalCard } from "./GrantApprovalCard";
import { AllocationMatrixInfoCard } from "./AllocationMatrixInfoCard";
import {
  calculateAllocationValue,
  getOriginalAllocationRule,
} from "../../utils/allocationMatrixValue";

// ── Note history helpers ──────────────────────────────────────────────────────

const NOTE_REGEX = /^\[([^|]+)\|([^|]+)\|([^\]]+)\]\s(.+)$/;

interface NoteEntryJson {
  state: string;
  author: string;
  date: string;
  text: string;
}

type NoteEntry =
  | {
      type: "structured";
      state: string;
      author: string;
      date: string;
      text: string;
    }
  | { type: "legacy"; text: string };

function parseNoteHistory(note: string): NoteEntry[] {
  if (!note) return [];
  try {
    const parsed = JSON.parse(note);
    if (Array.isArray(parsed)) {
      return parsed.map((e: NoteEntryJson) => ({
        type: "structured" as const,
        state: e.state ?? "",
        author: e.author ?? "",
        date: e.date ?? "",
        text: e.text ?? "",
      }));
    }
  } catch (_) {
    // not JSON — fall through to legacy parser
  }
  // Legacy fallback: line-by-line regex parsing
  return note
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const m = line.match(NOTE_REGEX);
      if (m) {
        return {
          type: "structured" as const,
          state: m[1].trim(),
          author: m[2].trim(),
          date: m[3].trim(),
          text: m[4].trim(),
        };
      }
      return { type: "legacy" as const, text: line };
    });
}

function NoteHistory({ note }: { note: string }) {
  const entries = parseNoteHistory(note);
  if (entries.length === 0) return null;

  const legacyEntries = entries.filter(
    (e) =>
      e.type === "legacy" ||
      (e.type === "structured" && !e.state && !e.author && !e.date)
  );
  const structuredEntries = entries.filter(
    (e) => e.type === "structured" && (e.state || e.author || e.date)
  );

  return (
    <VStack className="gap-2">
      {legacyEntries.length > 0 && (
        <div className="rounded-lg bg-gray-50 border border-borderColor px-3 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-light mb-1">
            Earlier notes
          </p>
          <VStack className="divide-y divide-borderColor">
            {legacyEntries.map((e, i) => (
              <p
                key={i}
                className="text-sm text-secondaryText py-1.5 first:pt-0 last:pb-0"
              >
                {e.text}
              </p>
            ))}
          </VStack>
        </div>
      )}
      {structuredEntries.map((entry, i) =>
        entry.type === "structured" ? (
          <div
            key={i}
            className="border border-borderColor rounded-lg p-3 bg-white"
          >
            <HStack className="items-center gap-1.5 mb-1 flex-wrap">
              {entry.state && (
                <span className="text-[11px] font-medium px-2 py-0.5 rounded-sm bg-blue-50 text-blue-600">
                  {stringFormat(entry.state)}
                </span>
              )}
              {entry.author && (
                <>
                  <span className="text-xs text-gray-light">·</span>
                  <span className="text-xs font-medium text-primaryText">
                    {entry.author}
                  </span>
                </>
              )}
              {entry.date && (
                <>
                  <span className="text-xs text-gray-light">·</span>
                  <span className="text-xs text-gray-light">{entry.date}</span>
                </>
              )}
            </HStack>
            <p className="text-sm text-secondaryText">{entry.text}</p>
          </div>
        ) : null
      )}
    </VStack>
  );
}

export function GrantCreated() {
  const currency = getCurrencyType();
  const { companyData } = useCompanyStore();
  const { accessToken } = useAuthStore();
  const { state: grantCreatedDialog, setState: setGrantCreatedDialog } =
    useGrantCreatedDialog();
  const [showDialog, setShowDialog] = useState(false);
  const [showApprovalWarning, setShowApprovalWarning] = useState(false);
  const pendingOverridePendingSignOffRef = useRef(false);
  const [showMessage, setShowMessage] = useState(false);
  const [lapseWithdrawed, setLapseWithdrawed] = useState(false);
  const { state: showSignaturePad, setState: setShowSignaturePad } =
    useSignaturePadDialog();
  const { grant, isFetching } = useGrant(grantCreatedDialog.grantId ?? "");
  const { data: _plans } = useEsopPlans();
  const errorMessage = useError();

  // Accordion state for "Grant added successfully" variant
  const [expanded, setExpanded] = useState(true);
  // Accordion states for the state change variant
  const [card1Expanded, setCard1Expanded] = useState(true);
  const [card2Expanded, setCard2Expanded] = useState(false);
  const [card3Expanded, setCard3Expanded] = useState(false);
  const [card4Expanded, setCard4Expanded] = useState(false);
  const [allocationMatrixExpanded, setAllocationMatrixExpanded] =
    useState(false);

  const [emailCheck, setEmailCheck] = useState(false);
  const [notes, setNotes] = useState("");
  const [noteError, setNoteError] = useState("");
  const [currentLoadingState, setCurrentLoadingState] = useState<string | null>(
    null
  );
  const [overrideAcceptanceWindow, setOverrideAcceptanceWindow] =
    useState(false);
  const [acceptanceNumber, setAcceptanceNumber] = useState("");

  useEffect(() => {
    if (isFetching) return;
    const plan = (_plans ?? []).filter(
      (plan) => plan.esopPlanId === grant?.planid
    )[0];
    if (plan?.esopPlanState === PlanState.AMENDMENT) {
      setShowDialog(true);
      errorMessage.setMessage(
        "Plan needs to be in active state to update the Grant State"
      );
    }
  }, [grant, isFetching]);

  const { mutate: changeOptionStates, isLoading } = useChangeOptionStates();
  const { mutate: attachSignature, isLoading: signatureBeingAttached } =
    useSignAndApproveGrant();
  const { mutate: createEsignRequest, isLoading: eSignSuccess } =
    useCreateESignRequest();
  const { data: emailSettings, isFetched } = useGetEmailSettings();
  const { data: matrices } = useGetAllMatrices();
  const { data: evalData } = useEvaluateMatrixQuery(
    grant?.allocationMatrixId,
    grant?.employeeId,
    // The evaluator must exclude the grant currently being reviewed. Without
    // this id, an already-persisted grant is counted as prior utilization and
    // its value is subtracted a second time in the status view.
    grant?.grantIdentifier
  );

  const matrixName =
    (matrices ?? []).find((m: any) => m.id === grant?.allocationMatrixId)
      ?.name ||
    grant?.allocationMatrixName ||
    "";
  const currentPlan = (_plans ?? []).find(
    (p) => p.esopPlanId === grant?.planid
  );
  const currentUserId = accessToken
    ? decodeAuthToken(accessToken).id?.toString()
    : undefined;
  const isCurrentUserApprover = !!(
    currentPlan?.grantApprovalConfig?.approvers ?? []
  ).some((a) => a.userId === currentUserId);
  const hasMultipleApprovers =
    (currentPlan?.grantApprovalConfig?.approvers ?? []).length > 1;
  const userIsAdmin = isAdmin();

  const { data: approvalRows } = useGetGrantApprovalStatus(
    grant?.optionHolderId ?? ""
  );
  const approvalConfig = currentPlan?.grantApprovalConfig;
  const approvalRequirementsMet = (() => {
    const approvers = approvalConfig?.approvers ?? [];
    if (!approvalConfig?.requireSignOff || approvers.length === 0) return true;
    const rows = approvalRows ?? [];
    if (approvalConfig.approvalOrder === "SEQUENTIAL") {
      return approvers.every(
        (a) => rows.find((r) => r.userId === a.userId)?.status === "APPROVED"
      );
    }
    const approvedCount = rows.filter((r) => r.status === "APPROVED").length;
    const required = approvalConfig.minApprovers ?? approvers.length;
    return approvedCount >= required;
  })();

  const resolvedRule = getOriginalAllocationRule(
    matrices,
    grant?.allocationMatrixId,
    grant?.matchedRule as any
  );
  const availableState = grant?.availableStates ?? [];
  const isCustomLetter = grant?.customizedGrantLetter ?? false;

  useEffect(() => {
    if (availableState.includes("OFFERED")) {
      setAcceptanceNumber(
        String(
          companyData?.companySettings?.grantLetterSettings?.acceptanceNumber ??
            0
        )
      );
    }
  }, [
    grant?.optionHolderId,
    availableState,
    companyData?.companySettings?.grantLetterSettings?.acceptanceNumber,
  ]);

  useEffect(() => {
    if (isFetching) return;
    if (isFetched && grant) {
      if (
        grant.optionHolderState === "APPROVED" ||
        grant.optionHolderState === "OFFERED"
      ) {
        setEmailCheck(
          (emailSettings?.sendEmailsToEmployees &&
            emailSettings?.sendGrantEmailsToEmployees &&
            emailSettings?.sendGrantOfferedEmailsToEmployees) ||
            false
        );
      } else if (grant.optionHolderState === "GRANTED") {
        setEmailCheck(
          (emailSettings?.sendEmailsToEmployees &&
            emailSettings?.sendGrantEmailsToEmployees &&
            emailSettings?.sendGrantWithdrawEmailsToEmployees) ||
            false
        );
      }
    }
  }, [grant, isFetched, isFetching]);

  const siteOfferedEmailsEnabled =
    emailSettings?.sendEmailsToEmployees &&
    emailSettings?.sendGrantEmailsToEmployees &&
    emailSettings?.sendGrantOfferedEmailsToEmployees;
  const generateCredentialsOnGrantOffer =
    emailSettings?.generateCredentialsOnGrantOffer ?? false;

  const requestApproverSignature =
    companyData?.companySettings?.grantLetterSettings?.requestApproverSignature;
  const showValueAtGrantToAdmin =
    companyData?.companySettings?.grantLetterSettings?.showValueAtGrantToAdmin;
  const useAllocationMatrixForGranting =
    companyData?.companySettings?.grantLetterSettings
      ?.useAllocationMatrixForGranting;

  // Permissible limit checks — For absolute-value-mode rules, the comparable value
  // should be: optionsGranted × (FMV - strikePrice) for GAIN, or optionsGranted × FMV for COST
  // grant.pricePerOption is the per-share FMV selected at grant creation, or the
  // fallback (public stock price / latest valuation) resolved server-side.
  // grant.sharePrice is the total VALUE AT GRANT, so it's used only if pricePerOption is missing.
  const perShareFMV =
    grant?.pricePerOption ??
    (grant?.optionsGranted && grant?.optionsGranted > 0
      ? (grant?.sharePrice ?? 0) / (grant.optionsGranted * (grant.ratio || 1))
      : 0);

  const isValueModeRule =
    resolvedRule?.allocationMode === AllocationMode.ABSOLUTE_VALUE;

  const latestFMV = Number((evalData as any)?.sharePrice ?? perShareFMV);
  const grantComparableValue = calculateAllocationValue({
    options: grant?.optionsGranted ?? 0,
    ratio: grant?.ratio,
    fmv: latestFMV,
    strikePrice: grant?.grantPrice ?? 0,
    allocationMode: resolvedRule?.allocationMode,
    costOrGain: resolvedRule?.costOrGain,
  });

  // For PER_EMPLOYEE scope, include allocated value from prior grants
  const isPerEmployeeScope = resolvedRule?.scope === RuleScope.PER_EMPLOYEE;
  const allocatedValue = isPerEmployeeScope
    ? evalData?.alreadyAllocated ?? evalData?.utilization?.allocated ?? 0
    : 0;
  const remainingMinimum =
    resolvedRule?.quantumMin != null
      ? Math.max(resolvedRule.quantumMin - allocatedValue, 0)
      : null;
  const remainingMaximum =
    resolvedRule?.quantumMax != null
      ? Math.max(resolvedRule.quantumMax - allocatedValue, 0)
      : null;

  const isAbovePermissibleLimit =
    useAllocationMatrixForGranting &&
    resolvedRule &&
    remainingMaximum != null &&
    grantComparableValue > remainingMaximum;

  const isBelowPermissibleLimit =
    useAllocationMatrixForGranting &&
    resolvedRule &&
    remainingMinimum != null &&
    grantComparableValue < remainingMinimum;

  const hasAllocationMatrix =
    useAllocationMatrixForGranting && !!grant?.allocationMatrixId;

  const isForApproval = grant?.optionHolderState === "FOR_APPROVAL";
  function handleClick() {
    if (!grant?.optionHolderId) return;
    changeOptionStates(
      {
        optionHolderIds: [grant.optionHolderId.toString()],
        optionHolderState: "FOR_APPROVAL",
        note: notes.trim(),
        mailToBeSent: emailCheck,
        lapseWithdrawed: false,
      },
      {
        onSuccess: () => {
          toast("State Changed Successfully!", {
            type: "success",
            autoClose: 2000,
          });
          setGrantCreatedDialog({ open: false });
        },
        onError: (err: any) => {
          errorMessage.setMessage(err.response.data.reason);
          toast(err.response.data.reason, { type: "error", autoClose: 2000 });
          setGrantCreatedDialog({ open: false });
        },
      }
    );
  }

  useEffect(() => {
    if (
      showSignaturePad.signatureType === "regular" &&
      !isCustomLetter &&
      showSignaturePad.signature
    ) {
      handleSignaturePad(showSignaturePad.signature);
    }
    if (showSignaturePad.signatureType === "esign") {
      createDigiESignRequest();
    }
  }, [showSignaturePad]);

  function createDigiESignRequest() {
    const eSignRequestObject = {
      entityIds: [grant?.optionHolderId.toString() ?? ""],
      type: "grant",
      ...(notes.trim() ? { note: notes.trim() } : {}),
    };
    setShowSignaturePad({ open: false, isLoading: !eSignSuccess });
    createEsignRequest(eSignRequestObject, {
      onSuccess: () => {
        setShowSignaturePad({ open: false, isLoading: eSignSuccess });
      },
      onError: (err: any) => {
        const error = err.response.data as ErrorInterface;
        toast(`${error.errors}`, { autoClose: 2000, type: "error" });
        setShowSignaturePad({ open: false, isLoading: eSignSuccess });
      },
    });
  }

  const handleSignaturePad = (signature: string) => {
    if (signature.includes("data:image/png;base64,")) {
      signature = signature.split(",")[1];
    }
    if (!signature || signature.trim() === "") {
      toast("Please select or generate a signature before saving.", {
        type: "error",
        autoClose: 2000,
      });
      return;
    }
    const signatureAttachmentObj: SignatureAttachment = {
      signature,
      entityIds: [grant?.optionHolderId.toString() ?? ""],
      origin: "grant",
      ...(notes.trim() ? { note: notes.trim() } : {}),
      ...(pendingOverridePendingSignOffRef.current
        ? { overridePendingSignOff: true }
        : {}),
    };
    setShowSignaturePad({ open: true, isLoading: !signatureBeingAttached });
    attachSignature(signatureAttachmentObj, {
      onSuccess: () => {
        toast("State Changed Successfully!", {
          type: "success",
          autoClose: 2000,
        });
        setShowSignaturePad({ open: false, isLoading: signatureBeingAttached });
        setGrantCreatedDialog({ open: false });
      },
      onError: (err: any) => {
        const error = err.response.data as ErrorInterface;
        toast(`${error.errors}`, { autoClose: 2000, type: "error" });
        setShowSignaturePad({ open: false, isLoading: signatureBeingAttached });
      },
    });
  };

  function updateGrantStatus(state: string, overridePendingSignOff = false) {
    setCurrentLoadingState(state);
    pendingOverridePendingSignOffRef.current = overridePendingSignOff;
    if (!grant?.optionHolderId || !state) return;
    const acceptanceDays = Number(acceptanceNumber);
    if (
      state === "OFFERED" &&
      overrideAcceptanceWindow &&
      (!Number.isInteger(acceptanceDays) || acceptanceDays < 0)
    ) {
      setNoteError("Acceptance window must be a whole number of days or zero");
      return;
    }
    if (requestApproverSignature && state === "APPROVED" && !isCustomLetter) {
      toast("Please Sign In order to approve the grant", {
        type: "info",
        autoClose: 2000,
      });
      setShowSignaturePad({
        open: true,
        entityType: "grant",
        ids: [grant.optionHolderId],
        userName: grant.optionHolderName,
      });
      return;
    }
    if (state === "WITHDRAW" && !showMessage) {
      setShowMessage(true);
      setCard4Expanded(true);
      return;
    }
    if (state === "WITHDRAW" && notes === "") {
      setNoteError("Note is required");
      setCard4Expanded(true);
      return;
    }
    changeOptionStates(
      {
        optionHolderIds: [grant.optionHolderId.toString()],
        optionHolderState: state,
        note: notes.trim(),
        mailToBeSent: emailCheck,
        ...(state === "OFFERED" && overrideAcceptanceWindow
          ? { acceptanceNumber: acceptanceDays }
          : {}),
        lapseWithdrawed,
        overridePendingSignOff,
      },
      {
        onSuccess: () => {
          toast("State Changed Successfully!", {
            type: "success",
            autoClose: 2000,
          });
          setGrantCreatedDialog({ open: false });
        },
        onError: (err: any) => {
          const error = err.response.data as ErrorInterface;
          errorMessage.setMessage(error.errorMessage);
          toast(error.errorMessage, { type: "error", autoClose: 2000 });
          setGrantCreatedDialog({ open: false });
        },
      }
    );
  }

  const alertDialogEl = (
    <AlertDialog
      open={showDialog}
      error
      message={errorMessage.message}
      primaryActionText="Retry"
      secondaryActionText="Go Back"
      onPrimaryAction={() => {
        errorMessage.reset();
        setGrantCreatedDialog({ open: false });
        setShowDialog(false);
      }}
      onSecondaryAction={() => {
        errorMessage.reset();
        setGrantCreatedDialog({ open: false });
        setShowDialog(false);
      }}
      onClose={() => {
        setGrantCreatedDialog({ open: false });
        errorMessage.reset();
        setShowDialog(false);
      }}
    />
  );

  const approvalWarningDialogEl = (
    <AlertDialog
      open={showApprovalWarning}
      message="Required approvals are not met. Are you sure you want to approve this grant?"
      primaryActionText="Approve Anyway"
      secondaryActionText="Cancel"
      onPrimaryAction={() => {
        setShowApprovalWarning(false);
        updateGrantStatus("APPROVED", true);
      }}
      onSecondaryAction={() => setShowApprovalWarning(false)}
      onClose={() => setShowApprovalWarning(false)}
    />
  );

  // ── New 3-card layout for ALL "state change" dialogs ──────────────────────
  if (grantCreatedDialog.variant === "state change") {
    if (isFetching) {
      return (
        <VStack className="items-center justify-center p-20">
          <CircularProgress size={32} />
        </VStack>
      );
    }

    return (
      <>
        {/* Header */}
        <div className="px-10 text-lg font-medium border-b py-7">
          <h6 className="flex justify-between">
            Update Grant Status
            <button
              className="cursor-pointer"
              onClick={() => setGrantCreatedDialog({ open: false })}
            >
              X
            </button>
          </h6>
        </div>

        <VStack className="px-10 py-6 gap-4">
          {(isAbovePermissibleLimit || isBelowPermissibleLimit) && (
            <InfoBox
              message={
                isAbovePermissibleLimit
                  ? "Granted options are above permissible limit. Please check notes before approving the grant."
                  : "Granted options are below permissible limit. Please check notes before approving the grant."
              }
            />
          )}

          <CollapsibleInfoCard
            expanded={card1Expanded}
            onToggle={() => setCard1Expanded((p) => !p)}
            title={
              <HStack className="items-center gap-2 flex-wrap">
                <span className="font-medium text-sm text-primaryText">
                  Grant Details
                </span>
                <StatusLabel state={grant?.optionHolderState ?? ""} />
                <GrantOfferAcceptanceBadge
                  state={grant?.optionHolderState}
                  offeredAt={grant?.grantOfferedAt}
                  daysRemaining={grant?.grantOfferDaysRemaining}
                  expiresAt={grant?.grantOfferExpiresAt}
                />
                {hasAllocationMatrix && (
                  <span
                    className="h-5 items-center rounded-sm font-medium text-center text-[11px] leading-5 px-2"
                    style={{
                      color: "rgba(77, 123, 255, 1)",
                      backgroundColor: "rgba(77, 123, 255, 0.1)",
                    }}
                  >
                    Allocation Matrix
                  </span>
                )}
              </HStack>
            }
          >
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <div className="text-xs text-gray-light mb-0.5">Grant By</div>
                <div className="text-sm font-medium text-primaryText">
                  Options
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-light mb-0.5">
                  Name of Holder
                </div>
                <div className="text-sm font-medium text-primaryText">
                  {grant?.optionHolderName}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-light mb-0.5">Grant ID</div>
                <div className="text-sm font-medium text-primaryText">
                  {grant?.grantIdentifier}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-light mb-0.5">Plan Name</div>
                <div className="text-sm font-medium text-primaryText">
                  {grant?.planName}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-light mb-0.5">Grant Date</div>
                <div className="text-sm font-medium text-primaryText">
                  {grant?.grantDate &&
                    formatDisplayDate(new Date(grant.grantDate).toUTCString())}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-light mb-0.5">Grant Type</div>
                <div className="text-sm font-medium text-primaryText">
                  {grant?.planType || grant?.grantType || "ESOP"}
                </div>
              </div>
              {hasAllocationMatrix && matrixName && (
                <div className="col-span-2">
                  <div className="text-xs text-gray-light mb-0.5">
                    Allocation Matrix
                  </div>
                  <div className="text-sm font-medium text-primaryText">
                    {matrixName}
                  </div>
                </div>
              )}
            </div>
          </CollapsibleInfoCard>

          {/* ── Card 2: Grant Details (options) ─────────────────────────── */}
          <CollapsibleInfoCard
            expanded={card2Expanded}
            onToggle={() => setCard2Expanded((p) => !p)}
            title={
              <span className="font-medium text-sm text-primaryText">
                Grant Details
              </span>
            }
            collapsedSummary={
              <>
                <span className="text-xs text-secondaryText">
                  No. Of Options: {grant?.optionsGranted?.toLocaleString()}
                </span>
                {isAbovePermissibleLimit && (
                  <>
                    <span className="text-xs text-gray-light">|</span>
                    <HStack className="items-center gap-0.5">
                      <Icon
                        icon="ri:alert-line"
                        height={12}
                        className="text-primary"
                      />
                      <span className="text-xs text-primary font-medium">
                        Above Permissible Limit
                      </span>
                    </HStack>
                  </>
                )}
                {isBelowPermissibleLimit && (
                  <>
                    <span className="text-xs text-gray-light">|</span>
                    <HStack className="items-center gap-0.5">
                      <Icon
                        icon="ri:alert-line"
                        height={12}
                        className="text-primary"
                      />
                      <span className="text-xs text-primary font-medium">
                        Below Permissible Limit
                      </span>
                    </HStack>
                  </>
                )}
                <span className="text-xs text-gray-light">|</span>
                <span className="text-xs text-secondaryText">
                  Grant Price:{" "}
                  {formatCurrency(grant?.grantPrice ?? 0, currency)}
                </span>
                {showValueAtGrantToAdmin && grant?.sharePrice && (
                  <>
                    <span className="text-xs text-gray-light">|</span>
                    <span className="text-xs text-secondaryText">
                      Value at Grant:{" "}
                      {formatCurrency(Number(grant.sharePrice), currency)}
                    </span>
                  </>
                )}
                {(showValueAtGrantToAdmin || isValueModeRule) && !!perShareFMV && (
                  <>
                    <span className="text-xs text-gray-light">|</span>
                    <span className="text-xs text-secondaryText">
                      Share Price: {formatCurrency(perShareFMV, currency)}
                    </span>
                  </>
                )}
              </>
            }
          >
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <div className="text-xs text-gray-light mb-0.5">
                  No. Of Options
                </div>
                <HStack className="items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-primaryText">
                    {getFormattedValue(
                      grant?.optionsGranted || 0,
                      currency,
                      grant?.isFractional
                    )}
                  </span>
                  {isAbovePermissibleLimit && (
                    <HStack className="items-center gap-0.5">
                      <Icon
                        icon="ri:alert-line"
                        height={14}
                        className="text-primary"
                      />
                      <span className="text-xs text-primary font-medium">
                        Above Permissible Limit
                      </span>
                    </HStack>
                  )}
                  {isBelowPermissibleLimit && (
                    <HStack className="items-center gap-0.5">
                      <Icon
                        icon="ri:alert-line"
                        height={14}
                        className="text-primary"
                      />
                      <span className="text-xs text-primary font-medium">
                        Below Permissible Limit
                      </span>
                    </HStack>
                  )}
                </HStack>
              </div>
              <div>
                <div className="text-xs text-gray-light mb-0.5">
                  Grant Price
                </div>
                <div className="text-sm font-medium text-primaryText">
                  {formatCurrency(grant?.grantPrice ?? 0, currency)}
                </div>
              </div>
              {showValueAtGrantToAdmin && grant?.sharePrice && (
                <div>
                  <div className="text-xs text-gray-light mb-0.5">
                    Value at Grant
                  </div>
                  <div className="text-sm font-medium text-primaryText">
                    {formatCurrency(Number(grant.sharePrice), currency)}
                  </div>
                </div>
              )}
              {(showValueAtGrantToAdmin || isValueModeRule) && !!perShareFMV && (
                <div>
                  <div className="text-xs text-gray-light mb-0.5">
                    Share Price
                  </div>
                  <div className="text-sm font-medium text-primaryText">
                    {formatCurrency(perShareFMV, currency)}
                  </div>
                </div>
              )}
              <div>
                <div className="text-xs text-gray-light mb-0.5">Vested</div>
                <div className="text-sm font-medium text-primaryText">
                  {getFormattedValue(
                    grant?.optionsVested || 0,
                    currency,
                    grant?.isFractional
                  )}
                </div>
              </div>
            </div>
          </CollapsibleInfoCard>

          {/* ── Card 3: Vesting Schedule ─────────────────────────────────── */}
          <CollapsibleInfoCard
            expanded={card3Expanded}
            onToggle={() => setCard3Expanded((p) => !p)}
            title={
              <span className="font-medium text-sm text-primaryText">
                Vesting Schedule
              </span>
            }
            collapsedSummary={
              <>
                {grant?.vestingTemplateName && (
                  <span className="text-xs text-secondaryText">
                    {grant.vestingTemplateName}
                  </span>
                )}
                {grant?.cliffPeriod != null && (
                  <>
                    <span className="text-xs text-gray-light">|</span>
                    <span className="text-xs text-secondaryText">
                      Cliff: {grant.cliffPeriod}M
                    </span>
                  </>
                )}
                {grant?.vestingDate && (
                  <>
                    <span className="text-xs text-gray-light">|</span>
                    <span className="text-xs text-secondaryText">
                      Start:{" "}
                      {formatDisplayDate(
                        new Date(grant.vestingDate).toUTCString()
                      )}
                    </span>
                  </>
                )}
              </>
            }
          >
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div>
                <div className="text-xs text-gray-light mb-0.5">
                  Vesting Template
                </div>
                <div className="text-sm font-medium text-primaryText">
                  {grant?.vestingTemplateName || "—"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-light mb-0.5">
                  Vesting Date Type
                </div>
                <div className="text-sm font-medium text-primaryText">
                  {capitalizeEachWord(
                    stringFormat(grant?.vestingDateType || "")
                  )}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-light mb-0.5">
                  Vesting Start Date
                </div>
                <div className="text-sm font-medium text-primaryText">
                  {grant?.vestingDate
                    ? formatDisplayDate(
                        new Date(grant.vestingDate).toUTCString()
                      )
                    : "—"}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-light mb-0.5">
                  Cliff Period
                </div>
                <div className="text-sm font-medium text-primaryText">
                  {grant?.cliffPeriod != null ? `${grant.cliffPeriod}M` : "—"}
                </div>
              </div>
            </div>
          </CollapsibleInfoCard>

          {/* ── Allocation Matrix Info Card ─────────────────────────────────── */}
          {useAllocationMatrixForGranting && resolvedRule && (
            <CollapsibleInfoCard
              expanded={allocationMatrixExpanded}
              onToggle={() => setAllocationMatrixExpanded((p) => !p)}
              divider
              title={
                <span className="font-medium text-sm text-primaryText">
                  Allocation Matrix
                </span>
              }
            >
              <AllocationMatrixInfoCard
                rule={resolvedRule}
                grantValue={grantComparableValue}
                isAboveLimit={isAbovePermissibleLimit}
                isBelowLimit={isBelowPermissibleLimit}
                employeeId={grant?.employeeId}
                evalData={evalData}
                sharePrice={perShareFMV}
                grantPrice={grant?.grantPrice ?? 0}
                currentGrantId={grant?.grantIdentifier || grant?.id}
              />
            </CollapsibleInfoCard>
          )}

          {grant?.optionHolderId && (
            <GrantApprovalCard
              grantId={grant.optionHolderId}
              isCurrentUserApprover={isCurrentUserApprover}
              currentUserId={currentUserId}
              planApprovalConfig={currentPlan?.grantApprovalConfig}
              recommendationInputMode={
                resolvedRule?.allocationMode === AllocationMode.ABSOLUTE_VALUE
                  ? "VALUE"
                  : "OPTIONS"
              }
              onClose={() => setGrantCreatedDialog({ open: false })}
            />
          )}

          {/* ── Card 4: Notes ────────────────────────────────────────────── */}
          <CollapsibleInfoCard
            expanded={card4Expanded}
            onToggle={() => setCard4Expanded((p) => !p)}
            title={
              <span className="font-medium text-sm text-primaryText">
                Notes
              </span>
            }
          >
            <VStack className="gap-4">
              {hasAllocationMatrix && grant?.remarks && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-light mb-2">
                    Allocation Matrix
                  </p>
                  <p className="text-sm text-secondaryText">{grant.remarks}</p>
                </div>
              )}

              {grant?.note && (
                <div>
                  {hasAllocationMatrix && grant?.remarks && (
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-light mb-2">
                      General
                    </p>
                  )}
                  <NoteHistory note={grant.note} />
                </div>
              )}

              {!grant?.note && !grant?.remarks && (
                <p className="text-sm text-gray-light italic">No notes yet.</p>
              )}

              <div>
                <Label className="text-sm font-normal">Add Note</Label>
                <TextArea
                  value={notes}
                  onChange={(e) => {
                    setNotes(e.target.value);
                    if (noteError) setNoteError("");
                  }}
                />
                {noteError !== "" && <Error text={noteError} />}
              </div>
            </VStack>
          </CollapsibleInfoCard>

          {availableState.includes("OFFERED") && (
            <>
              {generateCredentialsOnGrantOffer && (
                <InfoBox
                  variant="warning"
                  message="Moving this grant to Offered will generate employee portal credentials for the holder if they do not already have login credentials."
                />
              )}
              <HStack className="items-center justify-between gap-4 mt-3">
                <VStack className="gap-2 flex-col-reverse">
                  <SwitchButton
                    value={overrideAcceptanceWindow}
                    label="Override default acceptance window"
                    onClick={() =>
                      setOverrideAcceptanceWindow(!overrideAcceptanceWindow)
                    }
                  />
                </VStack>
                {overrideAcceptanceWindow && (
                  <div className="mt-3 w-72">
                    <Label className="text-sm font-normal">
                      Acceptance window (days)
                    </Label>
                    <Input
                      className=""
                      type="number"
                      min="0"
                      step="1"
                      value={acceptanceNumber}
                      onChange={(event) =>
                        setAcceptanceNumber(event.target.value)
                      }
                    />
                    <p className="mt-1 text-xs text-secondaryText">
                      Set to 0 to keep the offer open.
                    </p>
                  </div>
                )}
              </HStack>
            </>
          )}

          {/* ── Email toggle (APPROVED → OFFERED only) ──────────────────── */}
          {grant?.optionHolderState === "APPROVED" && (
            <div className="border border-borderColor rounded-lg p-4 flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-primaryText">
                    {availableState.includes("OFFERED")
                      ? "Send offer email to employee"
                      : "Send email to employee"}
                  </div>
                  <div className="text-xs text-secondaryText mt-0.5">
                    Notify the employee about this grant
                  </div>
                </div>
                <div className="flex items-center justify-end shrink-0">
                  <SwitchButton
                    value={emailCheck}
                    label=""
                    onClick={() => setEmailCheck(!emailCheck)}
                  />
                </div>
              </div>
              {!siteOfferedEmailsEnabled && emailCheck && (
                <InfoBox message="Employee emails for offered grants are currently disabled in site settings. Enabling this will send an email for this grant only." />
              )}
              {siteOfferedEmailsEnabled && !emailCheck && (
                <InfoBox message="Employee emails for offered grants are currently enabled in site settings. Disabling this will skip the email for this grant only." />
              )}
            </div>
          )}

          {/* ── Lapse toggle (WITHDRAW confirmation step) ────────────────── */}
          {showMessage && (
            <HStack className="gap-8 text-sm font-normal">
              <SwitchButton
                value={lapseWithdrawed}
                label="Lapsed Withdrawed options?"
                onClick={() => setLapseWithdrawed(!lapseWithdrawed)}
              />
            </HStack>
          )}
        </VStack>

        {/* ── Footer buttons ───────────────────────────────────────────────── */}
        <HStack className="justify-end px-10 py-6 border-t gap-4">
          {showMessage ? (
            <ButtonPrimary
              onClick={() => updateGrantStatus("WITHDRAW")}
              loading={isLoading}
            >
              Withdraw
            </ButtonPrimary>
          ) : isForApproval &&
            hasMultipleApprovers &&
            isCurrentUserApprover ? null : isForApproval ? (
            <>
              <ButtonPrimary1
                onClick={() => updateGrantStatus("NOT_APPROVED")}
                loading={currentLoadingState === "NOT_APPROVED" && isLoading}
              >
                Not Approved
              </ButtonPrimary1>
              <ButtonPrimary
                onClick={() => {
                  if (userIsAdmin && !approvalRequirementsMet) {
                    setShowApprovalWarning(true);
                    return;
                  }
                  updateGrantStatus("APPROVED");
                }}
                loading={
                  currentLoadingState === "APPROVED" &&
                  (isLoading || signatureBeingAttached)
                }
              >
                Approve
              </ButtonPrimary>
            </>
          ) : (
            <HStack className="flex-row-reverse gap-4">
              {availableState.map((state, index) =>
                index === 0 ? (
                  <ButtonPrimary
                    key={state}
                    onClick={() => updateGrantStatus(state)}
                    loading={currentLoadingState === state && isLoading}
                  >
                    {stringFormat(state)}
                  </ButtonPrimary>
                ) : (
                  <ButtonSecondary
                    key={state}
                    onClick={() => updateGrantStatus(state)}
                    disabled={currentLoadingState === state && isLoading}
                  >
                    {currentLoadingState === state && isLoading
                      ? "Please wait..."
                      : stringFormat(state)}
                  </ButtonSecondary>
                )
              )}
            </HStack>
          )}
        </HStack>

        {alertDialogEl}
        {approvalWarningDialogEl}
      </>
    );
  }

  // ── "Grant added successfully" variant — keep existing layout ─────────────
  return (
    <>
      <div className="px-10 text-lg font-medium border-b py-7">
        <h6 className="flex justify-between">
          Grant added successfully
          <button
            className="cursor-pointer"
            onClick={() => setGrantCreatedDialog({ open: false })}
          >
            X
          </button>
        </h6>
      </div>
      <VStack className="justify-between px-10 grow">
        <HStack
          aria-label="section"
          className="py-4 mb-10 border-b border-dashed"
        >
          <Box className="py-2 pr-4">
            <ExpandButton
              expanded={expanded}
              onClick={() => setExpanded((state) => !state)}
            />
          </Box>
          <VStack className="flex-1">
            <Box
              aria-label="section header"
              className="flex flex-row items-center gap-4 mb-2"
            >
              <Box>
                <h5 className="text-sm font-medium text-[#030303]">
                  Grant Details
                </h5>
                <h6 className="text-sm font-normal text-gray-light">
                  {grant?.planName}
                </h6>
              </Box>
              <StatusLabel state={grant?.optionHolderState ?? ""} />
              <GrantOfferAcceptanceBadge
                state={grant?.optionHolderState}
                offeredAt={grant?.grantOfferedAt}
                daysRemaining={grant?.grantOfferDaysRemaining}
                expiresAt={grant?.grantOfferExpiresAt}
              />
            </Box>
            {expanded && (
              <HStack aria-label="section body" className="flex-wrap grow">
                {[
                  {
                    field: "Name of the Holder",
                    value: grant?.optionHolderName,
                  },
                  {
                    field: "Granted",
                    value: getFormattedValue(
                      grant?.optionsGranted || 0,
                      currency,
                      grant?.isFractional
                    ),
                  },
                  {
                    field: "Grant Price",
                    value: formatCurrency(grant?.grantPrice ?? 0, currency),
                  },
                  {
                    field: "Grant Date",
                    value:
                      grant?.grantDate &&
                      formatDisplayDate(
                        new Date(grant.grantDate)?.toUTCString()
                      ),
                  },
                  ...(showValueAtGrantToAdmin
                    ? [
                        {
                          field: "Value at Grant",
                          value: formatCurrency(
                            Number(grant?.sharePrice),
                            currency
                          ),
                        },
                      ]
                    : []),
                  {
                    field: "Vested",
                    value: getFormattedValue(
                      grant?.optionsVested || 0,
                      currency,
                      grant?.isFractional
                    ),
                  },
                  { field: "Grant ID", value: grant?.grantIdentifier },
                  {
                    field: "Vested Schedule",
                    value: grant?.vestingTemplateName,
                  },
                  { field: "Plan Name", value: grant?.planName },
                  {
                    field: "Vesting Date Type",
                    value: capitalizeEachWord(
                      stringFormat(grant?.vestingDateType || "")
                    ),
                  },
                  {
                    field: "Vesting Start Date",
                    value:
                      grant?.vestingDate &&
                      formatDisplayDate(
                        new Date(grant.vestingDate).toUTCString()
                      ),
                  },
                  ...(useAllocationMatrixForGranting && matrixName
                    ? [{ field: "Allocation Matrix", value: matrixName }]
                    : []),
                ].map(({ field, value }) => (
                  <HStack key={field} className="w-1/2 p-1">
                    <span className="w-1/2 text-sm font-normal text-gray-400">
                      {field}
                    </span>
                    <span className="w-1/2 text-xs3 text-[#030303] font-medium">
                      {value?.toString()}
                    </span>
                  </HStack>
                ))}
              </HStack>
            )}
          </VStack>
        </HStack>
      </VStack>
      <div className="px-10 pb-4">
        <Label className="text-sm font-normal">Add Note</Label>
        <TextArea value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <HStack className="justify-between p-8">
        <ButtonPrimary1 onClick={() => setGrantCreatedDialog({ open: false })}>
          Close
        </ButtonPrimary1>
        <ButtonPrimary onClick={() => handleClick()}>
          Send For Approval
        </ButtonPrimary>
      </HStack>
      {alertDialogEl}
    </>
  );
}
